import React, { useRef, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export default function SpellingTest({ sessionId, role, studentName }) {
  const canvasRef = useRef(null);
  const typedRef = useRef(null);
  const drawing = useRef(false);
  const lastPos = useRef(null);
  const [currentWord, setCurrentWord] = useState(0);
  const [results, setResults] = useState([]);
  const [phase, setPhase] = useState('setup'); // setup, waiting, writing, submitted, done
  const [typedAnswer, setTypedAnswer] = useState('');
  const [studentSubmitted, setStudentSubmitted] = useState('');
  const [touchDevice, setTouchDevice] = useState(false);
  const [wordList, setWordList] = useState([]);
  const [newWord, setNewWord] = useState('');
  const strokeBuffer = useRef([]);
  const flushTimer = useRef(null);

  useEffect(() => {
    setTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  // ── Canvas helpers ──
  const getPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) * scaleX, y: (src.clientY - rect.top) * scaleY };
  };

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = 900;
    canvas.height = 180;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fffdf5';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#e8e0f5';
    ctx.lineWidth = 1;
    for (let y = 60; y < canvas.height; y += 60) {
      ctx.beginPath(); ctx.moveTo(20, y); ctx.lineTo(canvas.width - 20, y); ctx.stroke();
    }
    strokeBuffer.current = [];
  }, []);

  useEffect(() => {
    if (phase === 'writing' && touchDevice) { initCanvas(); }
    if (phase === 'writing' && !touchDevice && typedRef.current) { typedRef.current.focus(); }
  }, [phase, touchDevice, initCanvas]);

  // ── Tutor: see student canvas strokes live ──
  useEffect(() => {
    if (role !== 'tutor') return;
    if (phase !== 'writing' && phase !== 'submitted') return;
    const channel = supabase.channel(`sp-strokes-${sessionId}-${currentWord}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'spelling_strokes', filter: `session_id=eq.${sessionId}` },
        (payload) => {
          const s = payload.new;
          if (s.word_index !== currentWord) return;
          const canvas = canvasRef.current;
          if (!canvas) return;
          const ctx = canvas.getContext('2d');
          ctx.beginPath(); ctx.moveTo(s.x0, s.y0); ctx.lineTo(s.x1, s.y1);
          ctx.strokeStyle = '#1a1a1a'; ctx.lineWidth = 4; ctx.lineCap = 'round'; ctx.stroke();
        })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [role, sessionId, currentWord, phase]);

  // ── Tutor: see typed answer live ──
  useEffect(() => {
    if (role !== 'tutor') return;
    const channel = supabase.channel(`sp-typed-${sessionId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'spelling_typed', filter: `session_id=eq.${sessionId}` },
        (payload) => { if (payload.new) setStudentSubmitted(payload.new.answer || ''); })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [role, sessionId]);

  // ── Student: listen for tutor events ──
  useEffect(() => {
    if (role !== 'student') return;
    const channel = supabase.channel(`sp-events-${sessionId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'spelling_events', filter: `session_id=eq.${sessionId}` },
        (payload) => {
          const ev = payload.new;
          if (ev.event_type === 'call_word') {
            setCurrentWord(ev.word_index);
            setPhase('writing');
            setTypedAnswer('');
            setStudentSubmitted('');
            if (ev.word_list) setWordList(JSON.parse(ev.word_list));
          }
          if (ev.event_type === 'mark') {
            setResults(r => [...r, { word: ev.word, correct: ev.correct }]);
            setPhase(ev.is_last ? 'done' : 'waiting');
          }
          if (ev.event_type === 'done') setPhase('done');
        })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [role, sessionId]);

  // ── Flush canvas strokes ──
  const flushStrokes = useCallback(async () => {
    const strokes = [...strokeBuffer.current];
    if (!strokes.length) return;
    strokeBuffer.current = [];
    await supabase.from('spelling_strokes').insert(strokes.map(s => ({
      session_id: sessionId, word_index: currentWord,
      x0: s.x0, y0: s.y0, x1: s.x1, y1: s.y1,
    })));
  }, [sessionId, currentWord]);

  const startDraw = (e) => { if (role !== 'student' || !touchDevice || phase !== 'writing') return; e.preventDefault(); drawing.current = true; lastPos.current = getPos(e); };
  const doDraw = (e) => {
    if (!drawing.current || role !== 'student' || phase !== 'writing') return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath(); ctx.moveTo(lastPos.current.x, lastPos.current.y); ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1a1a1a'; ctx.lineWidth = 4; ctx.lineCap = 'round'; ctx.stroke();
    strokeBuffer.current.push({ x0: lastPos.current.x, y0: lastPos.current.y, x1: pos.x, y1: pos.y });
    lastPos.current = pos;
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flushStrokes, 80);
  };
  const stopDraw = () => { drawing.current = false; flushStrokes(); };

  // ── Student submits answer ──
  const submitAnswer = async () => {
    await flushStrokes();
    const answer = touchDevice ? '[handwritten]' : typedAnswer;
    await supabase.from('spelling_typed').upsert([{ session_id: sessionId, answer, word_index: currentWord }], { onConflict: 'session_id' });
    await supabase.from('spelling_events').insert([{ session_id: sessionId, event_type: 'student_submitted', word_index: currentWord }]);
    setPhase('submitted');
  };

  // ── Tutor: call a word ──
  const callWord = async (idx) => {
    setCurrentWord(idx);
    setPhase('writing');
    setStudentSubmitted('');
    setTypedAnswer('');
    await supabase.from('spelling_strokes').delete().eq('session_id', sessionId).eq('word_index', idx);
    await supabase.from('spelling_typed').delete().eq('session_id', sessionId);
    await supabase.from('spelling_events').insert([{
      session_id: sessionId, event_type: 'call_word', word_index: idx,
      word_list: JSON.stringify(wordList),
    }]);
    if (canvasRef.current) initCanvas();
  };

  // ── Tutor: mark a word ──
  const markWord = async (correct) => {
    const newResults = [...results, { word: wordList[currentWord], correct }];
    setResults(newResults);
    const isLast = currentWord + 1 >= wordList.length;
    await supabase.from('spelling_events').insert([{
      session_id: sessionId, event_type: 'mark',
      word_index: currentWord, word: wordList[currentWord],
      correct, is_last: isLast,
    }]);
    if (isLast) {
      setPhase('done');
    } else {
      setPhase('waiting');
      setCurrentWord(currentWord + 1);
    }
  };

  const score = results.filter(r => r.correct).length;
  const total = wordList.length;

  const btn = (bg, border, color) => ({
    flex: 1, padding: '12px 16px', background: bg, border: `2px solid ${border}`,
    borderRadius: 12, fontSize: 15, cursor: 'pointer', fontWeight: 'bold', color,
  });

  // ── TUTOR SETUP ──
  if (role === 'tutor' && phase === 'setup') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ background: '#E0D4F7', borderRadius: 16, padding: '18px 20px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: 12, fontSize: 15 }}>Set up spelling test for {studentName}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: '2px solid #c5aff0', fontSize: 14, outline: 'none' }}
              value={newWord} onChange={e => setNewWord(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && newWord.trim()) { setWordList(w => [...w, newWord.trim().toLowerCase()]); setNewWord(''); } }}
              placeholder="Type a word and press Enter"
              autoCorrect="off" spellCheck={false} autoComplete="off"
            />
            <button style={{ background: '#b57bee', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 16px', cursor: 'pointer', fontWeight: 'bold' }}
              onClick={() => { if (newWord.trim()) { setWordList(w => [...w, newWord.trim().toLowerCase()]); setNewWord(''); } }}>
              Add
            </button>
          </div>
          <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {wordList.map((w, i) => (
              <span key={i} style={{ background: '#fff', borderRadius: 20, padding: '4px 12px', fontSize: 13, border: '2px solid #c5aff0', display: 'flex', alignItems: 'center', gap: 6 }}>
                {w}
                <span style={{ cursor: 'pointer', color: '#c04060', fontWeight: 'bold' }} onClick={() => setWordList(wl => wl.filter((_, j) => j !== i))}>×</span>
              </span>
            ))}
          </div>
          {wordList.length > 0 && (
            <button style={{ ...btn('#b57bee', '#9b5cc7', '#fff'), flex: 'none', marginTop: 14, width: '100%' }} onClick={() => callWord(0)}>
              Start Test — {wordList.length} {wordList.length === 1 ? 'word' : 'words'} 🚀
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── DONE screen ──
  if (phase === 'done') {
    const pct = total > 0 ? Math.round((score / total) * 100) : 0;
    const emoji = pct === 100 ? '🏆' : pct >= 80 ? '🌟' : pct >= 60 ? '😊' : '💪';
    const msg = pct === 100 ? 'Perfect score! Amazing!' : pct >= 80 ? 'Great job — almost there!' : pct >= 60 ? 'Good effort! Keep practising!' : 'Keep going — you\'ve got this!';
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ background: '#FFF5C3', borderRadius: 16, padding: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: 48 }}>{emoji}</div>
          <div style={{ fontSize: 24, fontWeight: 'bold', marginTop: 8 }}>{score} / {total}</div>
          <div style={{ color: '#888', marginTop: 4 }}>{msg}</div>
          <div style={{ height: 12, borderRadius: 10, background: '#e8e0f5', overflow: 'hidden', margin: '12px 0' }}>
            <div style={{ height: '100%', borderRadius: 10, background: 'linear-gradient(90deg, #b57bee, #60b8e8)', width: `${pct}%`, transition: 'width 0.5s ease' }} />
          </div>
        </div>
        <div style={{ background: '#fff', borderRadius: 16, padding: '16px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          {results.map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f0e8ff', color: r.correct ? '#2e7d52' : '#c04060' }}>
              <span style={{ fontSize: 18 }}>{r.correct ? '✅' : '❌'}</span>
              <span style={{ fontWeight: 'bold' }}>{r.word}</span>
            </div>
          ))}
        </div>
        {role === 'tutor' && (
          <button style={{ ...btn('#b57bee', '#9b5cc7', '#fff'), flex: 'none' }} onClick={() => { setResults([]); setCurrentWord(0); setPhase('setup'); setWordList([]); setTypedAnswer(''); setStudentSubmitted(''); }}>
            New Test
          </button>
        )}
      </div>
    );
  }

  // ── ACTIVE TEST ──
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Progress */}
      <div style={{ background: '#E0D4F7', borderRadius: 16, padding: '14px 18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
          <span style={{ color: '#888' }}>Word {Math.min(results.length + 1, total)} of {total}</span>
          <span style={{ color: '#68c98a', fontWeight: 'bold' }}>✅ {score} correct</span>
        </div>
        <div style={{ height: 10, borderRadius: 10, background: '#d4c5f0', overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 10, background: 'linear-gradient(90deg, #b57bee, #60b8e8)', width: `${(results.length / total) * 100}%`, transition: 'width 0.4s' }} />
        </div>
        {role === 'tutor' && (phase === 'writing' || phase === 'submitted') && (
          <div style={{ marginTop: 8, fontSize: 13, color: '#b57bee' }}>
            Say the word aloud: <strong style={{ fontSize: 16 }}>{wordList[currentWord]}</strong>
          </div>
        )}
      </div>

      {/* Waiting for tutor to call next word */}
      {phase === 'waiting' && role === 'student' && (
        <div style={{ background: '#FFF5C3', borderRadius: 16, padding: '20px', textAlign: 'center', color: '#888', fontSize: 15 }}>
          ⏳ Waiting for your tutor to call the next word...
        </div>
      )}

      {phase === 'waiting' && role === 'tutor' && (
        <div style={{ background: '#C9EDD4', borderRadius: 16, padding: '16px 20px', textAlign: 'center' }}>
          <div style={{ marginBottom: 10, fontSize: 14 }}>Ready for word {results.length + 1}? <strong>{wordList[currentWord]}</strong></div>
          <button style={{ ...btn('#68c98a', '#4caf76', '#fff'), flex: 'none', padding: '10px 24px' }} onClick={() => callWord(currentWord)}>
            Call word 📢
          </button>
        </div>
      )}

      {/* Student writing */}
      {phase === 'writing' && role === 'student' && (
        <div style={{ background: '#FFF5C3', borderRadius: 16, padding: '18px 20px' }}>
          <div style={{ fontSize: 14, color: '#888', marginBottom: 10, textAlign: 'center' }}>
            {touchDevice ? 'Write the word with your finger or stylus 👇' : 'Type the word below — no peeking! 👇'}
          </div>
          {touchDevice ? (
            <div style={{ borderRadius: 12, overflow: 'hidden', border: '2px solid #e8e0f5' }}>
              <canvas ref={canvasRef} style={{ width: '100%', display: 'block', touchAction: 'none' }}
                onTouchStart={startDraw} onTouchMove={doDraw} onTouchEnd={stopDraw} />
            </div>
          ) : (
            <input
              ref={typedRef}
              type="text"
              value={typedAnswer}
              onChange={e => setTypedAnswer(e.target.value)}
              style={{ width: '100%', padding: 14, borderRadius: 12, border: '2px solid #e8e0f5', fontSize: 22, textAlign: 'center', outline: 'none', letterSpacing: 3 }}
              autoCorrect="off" autoCapitalize="off" autoComplete="off"
              spellCheck={false} data-gramm="false" data-gramm_editor="false" data-enable-grammarly="false"
              placeholder="Type here..."
              onKeyDown={e => { if (e.key === 'Enter' && (typedAnswer.trim() || touchDevice)) submitAnswer(); }}
            />
          )}
          <button style={{ ...btn('#b57bee', '#9b5cc7', '#fff'), flex: 'none', marginTop: 12, width: '100%' }}
            onClick={submitAnswer} disabled={!touchDevice && !typedAnswer.trim()}>
            Submit ✓
          </button>
        </div>
      )}

      {/* Student waiting after submitting */}
      {phase === 'submitted' && role === 'student' && (
        <div style={{ background: '#C9EDD4', borderRadius: 16, padding: '20px', textAlign: 'center', color: '#2e7d52', fontSize: 15 }}>
          ✅ Submitted! Waiting for your tutor to mark it...
        </div>
      )}

      {/* Tutor marking view */}
      {(phase === 'writing' || phase === 'submitted') && role === 'tutor' && (
        <div style={{ background: '#fff', borderRadius: 16, padding: '18px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          {touchDevice ? (
            <>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 8 }}>Student's handwriting:</div>
              <div style={{ borderRadius: 12, overflow: 'hidden', border: '2px solid #e8e0f5', background: '#fffdf5' }}>
                <canvas ref={canvasRef} style={{ width: '100%', display: 'block' }} />
              </div>
            </>
          ) : (
            <div style={{ fontSize: 14, color: '#555', marginBottom: 8 }}>
              Student typed: <strong style={{ fontSize: 20, letterSpacing: 2, color: '#1a1a1a' }}>{studentSubmitted || '...'}</strong>
              {phase === 'writing' && <span style={{ marginLeft: 8, fontSize: 12, color: '#aaa' }}>(still writing)</span>}
              {phase === 'submitted' && <span style={{ marginLeft: 8, fontSize: 12, color: '#68c98a' }}>✓ submitted</span>}
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
            <button style={btn('#C9EDD4', '#68c98a', '#2e7d52')} onClick={() => markWord(true)}>✅ Correct</button>
            <button style={btn('#FFD6E0', '#f48fb1', '#c04060')} onClick={() => markWord(false)}>❌ Wrong</button>
          </div>
        </div>
      )}
    </div>
  );
}
